import type { AuthAction } from './types.js';

export const AUTH_MESSAGE_VERSION = '1';

export interface BuildAuthMessageOptions {
  domain: string;
  ownerPubkey: string;
  action: AuthAction;
  multisigPda: string | null;
  cluster: string;
  issuedAt: Date | string;
  expiresAt: Date | string;
  nonce: string;
  fields?: Record<string, string | number>;
}

export interface ParsedAuthMessage {
  domain: string;
  ownerPubkey: string;
  action: AuthAction;
  multisigPda: string;
  version: string;
  chain: string;
  issuedAt: string;
  expiresAt: string;
  nonce: string;
  fields: Record<string, string>;
}

export class AuthMessageError extends Error {
  override name = 'AuthMessageError';
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

const RESERVED_FIELD_KEYS = new Set([
  'Action',
  'Multisig',
  'Version',
  'Chain',
  'Issued At',
  'Expires At',
  'Nonce',
]);

function toIso(value: Date | string): string {
  if (value instanceof Date) return value.toISOString();
  const t = Date.parse(value);
  if (Number.isNaN(t)) {
    throw new AuthMessageError('BAD_TIMESTAMP', `invalid timestamp: ${value}`);
  }
  return new Date(t).toISOString();
}

export function buildAuthMessage(opts: BuildAuthMessageOptions): string {
  if (!/^[a-z0-9.-]+$/i.test(opts.domain)) {
    throw new AuthMessageError('BAD_DOMAIN', `invalid domain: ${opts.domain}`);
  }
  if (!opts.ownerPubkey) {
    throw new AuthMessageError('BAD_OWNER', 'ownerPubkey required');
  }
  if (!/^[a-f0-9]{16,}$/i.test(opts.nonce)) {
    throw new AuthMessageError('BAD_NONCE', 'nonce must be hex (>=16 chars)');
  }
  const issuedIso = toIso(opts.issuedAt);
  const expiresIso = toIso(opts.expiresAt);
  if (Date.parse(expiresIso) <= Date.parse(issuedIso)) {
    throw new AuthMessageError('BAD_EXPIRY', 'expiresAt must be after issuedAt');
  }
  if (opts.fields) {
    for (const key of Object.keys(opts.fields)) {
      if (RESERVED_FIELD_KEYS.has(key)) {
        throw new AuthMessageError('RESERVED_KEY', `field key reserved: ${key}`);
      }
      if (!/^[A-Z][A-Za-z0-9 ]*$/.test(key)) {
        throw new AuthMessageError(
          'BAD_FIELD_KEY',
          `field keys must be Title Case alpha-numeric: ${key}`
        );
      }
    }
  }

  const lines: string[] = [];
  lines.push(`${opts.domain} wants you to authorize an action with your Solana account:`);
  lines.push(opts.ownerPubkey);
  lines.push('');
  lines.push(`Action: ${opts.action}`);
  lines.push(`Multisig: ${opts.multisigPda ?? '-'}`);
  lines.push(`Version: ${AUTH_MESSAGE_VERSION}`);
  lines.push(`Chain: solana:${opts.cluster}`);
  lines.push(`Issued At: ${issuedIso}`);
  lines.push(`Expires At: ${expiresIso}`);
  lines.push(`Nonce: ${opts.nonce}`);
  if (opts.fields) {
    const sortedKeys = Object.keys(opts.fields).sort();
    for (const key of sortedKeys) {
      const v = opts.fields[key];
      if (v === undefined) continue;
      lines.push(`${key}: ${v}`);
    }
  }
  return lines.join('\n');
}

const PREAMBLE_RE =
  /^([a-z0-9.-]+) wants you to authorize an action with your Solana account:$/i;

export function parseAuthMessage(text: string): ParsedAuthMessage {
  const lines = text.split('\n');
  if (lines.length < 9) {
    throw new AuthMessageError('TOO_SHORT', 'message too short');
  }
  const preambleMatch = lines[0]!.match(PREAMBLE_RE);
  if (!preambleMatch) {
    throw new AuthMessageError('BAD_PREAMBLE', 'preamble line malformed');
  }
  const domain = preambleMatch[1]!;
  const ownerPubkey = lines[1]!.trim();
  if (!ownerPubkey) throw new AuthMessageError('BAD_OWNER', 'owner line empty');
  if (lines[2] !== '') throw new AuthMessageError('BAD_BLANK', 'blank separator missing');

  const fields: Record<string, string> = {};
  let parsedAction: string | undefined;
  let parsedMultisig: string | undefined;
  let parsedVersion: string | undefined;
  let parsedChain: string | undefined;
  let parsedIssued: string | undefined;
  let parsedExpires: string | undefined;
  let parsedNonce: string | undefined;
  for (let i = 3; i < lines.length; i++) {
    const line = lines[i]!;
    if (line === '' && i === lines.length - 1) continue;
    const idx = line.indexOf(':');
    if (idx === -1) {
      throw new AuthMessageError('BAD_LINE', `malformed line ${i}: ${line}`);
    }
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    switch (key) {
      case 'Action':
        if (parsedAction !== undefined) throw new AuthMessageError('DUP', 'duplicate Action');
        parsedAction = value;
        break;
      case 'Multisig':
        if (parsedMultisig !== undefined) throw new AuthMessageError('DUP', 'duplicate Multisig');
        parsedMultisig = value;
        break;
      case 'Version':
        if (parsedVersion !== undefined) throw new AuthMessageError('DUP', 'duplicate Version');
        parsedVersion = value;
        break;
      case 'Chain':
        if (parsedChain !== undefined) throw new AuthMessageError('DUP', 'duplicate Chain');
        parsedChain = value;
        break;
      case 'Issued At':
        if (parsedIssued !== undefined) throw new AuthMessageError('DUP', 'duplicate Issued At');
        parsedIssued = value;
        break;
      case 'Expires At':
        if (parsedExpires !== undefined) throw new AuthMessageError('DUP', 'duplicate Expires At');
        parsedExpires = value;
        break;
      case 'Nonce':
        if (parsedNonce !== undefined) throw new AuthMessageError('DUP', 'duplicate Nonce');
        parsedNonce = value;
        break;
      default:
        if (key in fields) {
          throw new AuthMessageError('DUP_FIELD', `duplicate field: ${key}`);
        }
        fields[key] = value;
    }
  }

  if (
    parsedAction === undefined ||
    parsedMultisig === undefined ||
    parsedVersion === undefined ||
    parsedChain === undefined ||
    parsedIssued === undefined ||
    parsedExpires === undefined ||
    parsedNonce === undefined
  ) {
    throw new AuthMessageError('MISSING_FIELD', 'missing one of required header fields');
  }

  if (parsedVersion !== AUTH_MESSAGE_VERSION) {
    throw new AuthMessageError('BAD_VERSION', `unsupported version: ${parsedVersion}`);
  }
  if (!parsedChain.startsWith('solana:')) {
    throw new AuthMessageError('BAD_CHAIN', `bad chain: ${parsedChain}`);
  }
  if (Number.isNaN(Date.parse(parsedIssued)) || Number.isNaN(Date.parse(parsedExpires))) {
    throw new AuthMessageError('BAD_TIMESTAMP', 'bad timestamps');
  }
  if (Date.parse(parsedExpires) <= Date.parse(parsedIssued)) {
    throw new AuthMessageError('BAD_EXPIRY', 'expiresAt must be after issuedAt');
  }
  if (!/^[a-f0-9]{16,}$/i.test(parsedNonce)) {
    throw new AuthMessageError('BAD_NONCE', 'nonce must be hex (>=16 chars)');
  }

  return {
    domain,
    ownerPubkey,
    action: parsedAction as AuthAction,
    multisigPda: parsedMultisig,
    version: parsedVersion,
    chain: parsedChain,
    issuedAt: parsedIssued,
    expiresAt: parsedExpires,
    nonce: parsedNonce,
    fields,
  };
}

export function randomNonceHex(byteLen = 8): string {
  const arr = new Uint8Array(byteLen);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < byteLen; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}
